// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/MondayMusicians.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FiUsers,
  FiVideo,
  FiCalendar,
  FiHeart,
  FiMessageCircle,
  FiMic,
  FiArrowLeft,
} from "react-icons/fi";
import Button from "../../components/Common/Button";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.accent};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const SessionHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing["2xl"]};
`;

const HighlightCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  padding: ${(props) => props.theme.spacing.xl};
  border-radius: 12px;
  text-align: center;
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    background: ${(props) => props.theme.colors.accent}20;
    transform: translateY(-2px);
  }
`;

const HighlightIcon = styled.div`
  font-size: ${(props) => props.theme.fontSizes["2xl"]};
  color: ${(props) => props.theme.colors.accent};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const HighlightTitle = styled.h4`
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${(props) => props.theme.colors.accent};
    font-size: ${(props) => props.theme.fontSizes.xl};
  }
`;

const TestimonialBox = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["2xl"]};
  border-radius: 12px;
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
`;

const ActivityList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    padding: ${(props) => props.theme.spacing.sm} 0;
    color: ${(props) => props.theme.colors.homeText};
    position: relative;
    padding-left: ${(props) => props.theme.spacing["2xl"]};

    &::before {
      content: "🎵";
      position: absolute;
      left: 0;
      top: ${(props) => props.theme.spacing.sm};
    }
  }
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const MondayMusicians = () => {
  const highlights = [
    {
      icon: <FiUsers />,
      title: "Sharing",
      description: "Open forum to share experiences and challenges",
    },
    {
      icon: <FiHeart />,
      title: "Caring",
      description: "Supporting each other through life's ups and downs",
    },
    {
      icon: <FiMessageCircle />,
      title: "Confessing",
      description: "Safe space for honesty and vulnerability",
    },
    {
      icon: <FiMic />,
      title: "Encouraging",
      description: "Building each other up in faith and ministry",
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Monday Musicians - The Music Church</title>
        <meta
          name="description"
          content="Monday Musicians - Weekly Zoom group therapy sessions for musicians and ministry workers. The least are the greatest, sharing, caring, and supporting each other."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              Monday Musicians
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              The day after church, we come together
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/ministries/monday-musicians-interior.png"
          alt="Monday Musicians panel in a stone courtyard with curved wooden seating, cameras, audience microphones, and speakers"
          maxWidth="1200px"
          backgroundColor="#071c2d"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About Monday Musicians</SectionTitle>
                <Text>
                  Monday Musicians is our weekly Zoom group therapy session held
                  every Monday - the day after church. It's a sacred space where
                  the least are the greatest, where we come together for
                  sharing, caring, confessing, comforting, exhorting, crying,
                  mourning, laughing, and rejoicing.
                </Text>
                <Text>
                  In this open forum, we admonish each other, debate issues,
                  give progress reports, and pray for one another. We discuss
                  our differences but remain open-minded to The Holy Spirit, Who
                  always shows up. We never plan how it begins or how it will
                  end, but it always ends well.
                </Text>
              </Section>

              <QuoteBox>
                "Therefore encourage one another and build each other up, just
                as in fact you are doing."
                <br />- 1 Thessalonians 5:11
              </QuoteBox>

              <Section>
                <SectionTitle>What Happens in Our Sessions</SectionTitle>
                <SessionHighlights>
                  {highlights.map((highlight, index) => (
                    <HighlightCard
                      key={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      <HighlightIcon>{highlight.icon}</HighlightIcon>
                      <HighlightTitle>{highlight.title}</HighlightTitle>
                      <p
                        style={{
                          fontSize: "0.875rem",
                          color: "#c6d9e2",
                        }}
                      >
                        {highlight.description}
                      </p>
                    </HighlightCard>
                  ))}
                </SessionHighlights>
              </Section>

              <TestimonialBox
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h3 style={{ marginBottom: "1rem" }}>A Safe Haven</h3>
                <p style={{ lineHeight: "1.8" }}>
                  "Monday Musicians has become my lifeline. It's where I can be
                  real, where I can share my struggles without judgment, and
                  where I always leave feeling encouraged and renewed."
                </p>
                <p
                  style={{
                    marginTop: "1rem",
                    fontStyle: "italic",
                    opacity: 0.8,
                  }}
                >
                  - A grateful member
                </p>
              </TestimonialBox>

              <Section>
                <SectionTitle>The Heart of Our Gathering</SectionTitle>
                <Text>
                  Monday Musicians is more than just a meeting - it's a family.
                  We understand the unique challenges of ministry life, the
                  pressures of performance, and the spiritual battles we face.
                  Here, we find strength in vulnerability and power in unity.
                </Text>
                <ActivityList>
                  <li>Correcting and being corrected with love</li>
                  <li>Celebrating victories and mourning losses together</li>
                  <li>Sharing testimonies and breakthroughs</li>
                  <li>Offering practical advice and spiritual wisdom</li>
                  <li>Creating accountability partnerships</li>
                  <li>Experiencing the move of the Holy Spirit</li>
                </ActivityList>
              </Section>

              <Section>
                <SectionTitle>No Hierarchy, Just Family</SectionTitle>
                <Text>
                  In Monday Musicians, titles don't matter. Whether you're a
                  worship leader, a backup singer, a sound engineer, or someone
                  who simply loves music and ministry - you're family here. The
                  least among us are the greatest, and everyone's voice matters.
                </Text>
                <Text>
                  Our sessions are organic and Spirit-led. We come with open
                  hearts, ready to give and receive, knowing that iron sharpens
                  iron and that we're better together than alone.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Join Us
                </SectionTitle>
                <InfoItem>
                  <FiCalendar />
                  <span>Every Monday</span>
                </InfoItem>
                <InfoItem>
                  <FiVideo />
                  <span>Via Zoom</span>
                </InfoItem>
                <InfoItem>
                  <FiUsers />
                  <span>Open to all musicians & ministry workers</span>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  What to Expect
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  Come as you are. No preparation needed, just an open heart.
                  Sessions typically include:
                </Text>
                <ul
                  style={{
                    fontSize: "0.875rem",
                    color: "#c6d9e2",
                    paddingLeft: "1.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <li>Opening prayer</li>
                  <li>Check-ins & sharing</li>
                  <li>Group discussion</li>
                  <li>Prayer requests</li>
                  <li>Encouragement & ministry</li>
                  <li>Closing prayer</li>
                </ul>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Confidentiality
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem" }}>
                  What's shared in Monday Musicians stays in Monday Musicians.
                  We maintain strict confidentiality to ensure everyone feels
                  safe to be authentic and vulnerable.
                </Text>
              </InfoCard>

              <InfoCard style={{ background: "#0d293b" }}>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Remember
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem", fontStyle: "italic" }}>
                  "Bear one another's burdens, and so fulfill the law of
                  Christ."
                </Text>
                <Text style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                  - Galatians 6:2
                </Text>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              You Don't Have to Do This Alone
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
              Join our Monday Musicians family and find the support,
              encouragement, and community you've been looking for.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="accent"
                size="large"
                icon={<FiVideo />}
                href="https://us02web.zoom.us/j/2897643954?pwd=L2dOSEMzd3F5T0xqZ3lWNnFDSGRFdz09"
                target="_blank"
                style={{ whiteSpace: "normal", textAlign: "center" }}
              >
                Join Monday Musicians on Zoom
              </Button>
              <Button
                variant="outline"
                size="large"
                style={{ borderColor: "white", color: "white" }}
                onClick={() => console.log("Learn more clicked")}
              >
                Learn More
              </Button>
            </div>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default MondayMusicians;
